import { UserSlice } from "@/store/user";
import { createStore } from "@/utils/store";
import { FeatureKey, FeatureLimit, Plan } from "@/api";
import { Bootstrap } from "@/store";

type State = {
  limits: Record<FeatureKey, FeatureLimit>;
};

type Slices = {
  userSlice: UserSlice;
};

type Actions = {
  /**
   * Gets the feature limit value based on the user's plan.
   * @param feature - The feature key.
   * @returns The feature limit value.
   */
  getFeatureLimit: (feature: FeatureKey) => number;

  /**
   * Gets the feature limit object for a given feature.
   * @param feature - The feature key.
   * @returns The feature limit object.
   */
  getFeatureLimitObject: (feature: FeatureKey) => FeatureLimit;

  /**
   * Updates the current value of a feature limit.
   * @param feature - The feature key.
   * @param value - The new current value.
   */
  updateFeatureLimitCurrent: (feature: FeatureKey, value: number) => void;
};

// We're only exposing the actions of this slice to stop direct state access.
export type FeatureLimitSlice = Actions;

export function createFeatureLimitSlice(initialState: Partial<Bootstrap>["limits"], userSlice: UserSlice) {
  return createStore<State, {}, Actions, Slices>({
    initialState: {
      limits: initialState as Record<FeatureKey, FeatureLimit>,
    },
    slices: () => ({
      userSlice,
    }),
    actions: ({ store, slices }) => {
      function getFeatureLimit(feature: FeatureKey) {
        const userPlan = slices.userSlice.isPro() ? Plan.PRO : Plan.FREE;
        const limit = store.limits.value[feature];
        return limit?.limit?.[userPlan] || 0;
      }

      function getFeatureLimitObject(feature: FeatureKey) {
        const limit = store.limits.value[feature];
        if (!limit) return limit;

        return {
          limit: {
            [Plan.FREE]: limit.limit[Plan.FREE] ?? 0,
            [Plan.PRO]: limit.limit[Plan.PRO] ?? 0,
          },
          current: limit.current ?? 0
        };
      }

      return {
        getFeatureLimit,
        getFeatureLimitObject,
        updateFeatureLimitCurrent: (feature: FeatureKey, value: number) => {
          const featureLimit = getFeatureLimitObject(feature);
          if (!featureLimit) {
            return;
          }

          const updatedLimit: FeatureLimit = {
            limit: {
              [Plan.FREE]: featureLimit.limit[Plan.FREE] ?? 0,
              [Plan.PRO]: featureLimit.limit[Plan.PRO] ?? 0,
            },
            current: value
          };

          // Update the feature limit with the new object
          store.limits.value[feature] = updatedLimit;
        },
      };
    },
    networkState: {},
  });
}
